import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import "./ResumeUploadDropzone.css";

const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function ResumeUploadDropzone({
  name,
  id,
  accept = ".pdf,.doc,.docx",
  selectedFile,
  onChange,
  onRemove,
  disabled,
  error,
  required = false,
  width = "100%",
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sizeError, setSizeError] = useState("");

  const inputId = id || name;

  const emitChange = (file) => {
    if (!file) {
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setSizeError(`File must be ${MAX_SIZE_MB} MB or smaller.`);
      return;
    }

    setSizeError("");
    onChange({
      target: {
        name,
        files: [file],
      },
    });
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    emitChange(file);
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) {
      return;
    }

    const file = event.dataTransfer.files?.[0];
    emitChange(file);
  };

  const handleRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSizeError("");
    onRemove?.();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const zoneClassName = [
    "resume-upload-dropzone",
    isDragging ? "resume-upload-dropzone--dragging" : "",
    error || sizeError ? "resume-upload-dropzone--error" : "",
    selectedFile ? "resume-upload-dropzone--has-file" : "",
    disabled ? "resume-upload-dropzone--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="resume-upload" style={{ width }}>
      <label htmlFor={inputId} className="resume-upload__label easy-apply-form__label">
        Resume
        {required ? (
          <span className="resume-upload__required easy-apply-form__required"> *</span>
        ) : null}
      </label>

      <input
        ref={inputRef}
        type="file"
        id={inputId}
        name={name}
        accept={accept}
        disabled={disabled}
        className="resume-upload__input"
        onChange={handleInputChange}
      />

      <label
        htmlFor={inputId}
        className={zoneClassName}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {selectedFile ? (
          <div className="resume-upload__selected">
            <CheckCircleOutlineIcon className="resume-upload__selected-icon" />
            <span className="resume-upload__filename">{selectedFile.name}</span>
            <button
              type="button"
              className="resume-upload__remove"
              onClick={handleRemove}
              aria-label="Remove resume"
            >
              <CancelIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <>
            <DescriptionOutlinedIcon className="resume-upload__icon" />
            <p className="resume-upload__text">
              <span className="resume-upload__text-link">Click to upload</span>
              {" or drag and drop"}
            </p>
            <p className="resume-upload__hint">PDF or Word • Max {MAX_SIZE_MB} MB</p>
          </>
        )}
      </label>

      {(error || sizeError) && (
        <p className="resume-upload__error">
          {sizeError || "Please upload the resume"}
        </p>
      )}
    </div>
  );
}

ResumeUploadDropzone.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  accept: PropTypes.string,
  selectedFile: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  required: PropTypes.bool,
  width: PropTypes.string,
};

export default ResumeUploadDropzone;
